import { defineStore } from 'pinia'
import { ref } from 'vue'
import { inboxApi, type InboxCounts, type InboxDoneResult, type InboxThreadRow } from '@/lib/inbox-api'
import { apiErrorMessage, isDemoRejection } from '@/lib/formErrors'
import { applyRealtime, listKey, listParams, type InboxState } from '@/views/inbox/inboxModel'
import { useAuthStore } from './auth'
import { useNotificationStore } from './notifications'
import type { WaThreadUpdatePayload } from '@/types/whatsapp'

/** Counts requests are folded into at most one per this window (socket bursts, the nav badge). */
export const COUNTS_DEBOUNCE_MS = 1_500

/** `moreError` when a failed Load more had no message of its own (the list says what failed). */
export const LOAD_MORE_FALLBACK = 'Could not load more conversations.'

/**
 * The unified inbox list (WhatsApp + email threads, newest first, cursor-paged), the "waiting for
 * a reply" counts the tabs and the nav badge show, and the done toggle. Opening and replying to a
 * thread stay in the per-channel stores (whatsapp.ts, outreach.ts); `patchThread` lets them keep
 * the row in step (unread cleared on open, snippet after a reply).
 */
export const useInboxStore = defineStore('inbox', () => {
  const notify = useNotificationStore()
  const auth = useAuthStore()

  const threads = ref<InboxThreadRow[]>([])
  const nextCursor = ref<string | null>(null)
  const counts = ref<InboxCounts | null>(null)
  /** A first page is loading (not set by a silent refresh). */
  const loading = ref(false)
  const loadingMore = ref(false)
  /** The last first-page request failed (the list's own error, shown above the rows). */
  const error = ref<string | null>(null)
  /** The last Load more failed (shown by the Load more button, where the user is looking). */
  const moreError = ref<string | null>(null)

  // ── list ──────────────────────────────────────────────────────────────

  /** Bumped by every first-page request: an answer (or a later page) for an older one is dropped. */
  let listSeq = 0
  /** A first-page request (silent ones included) has not answered yet: Load more waits for it. */
  let firstPagePending = false
  /** Which list `threads` holds (org + list params), so a failed load of ANOTHER list never leaves
   *  the previous list's rows on screen. */
  let rowsKey = ''

  const activeOrg = () => auth.activeOrgId ?? auth.user?.orgId ?? ''
  const keyFor = (state: InboxState) => `${activeOrg()}|${listKey(state)}`

  /**
   * Load the first page for `state`, or with `append` the next page after `nextCursor`. Resolves
   * true when this request's answer was applied (false: superseded, skipped or failed).
   *
   * - The latest first-page request wins; an older answer, or a page appended before a newer
   *   first page was asked for, is dropped.
   * - `append` is skipped without a next page, while a page is appending, and while any first
   *   page (a silent refresh too) is in flight.
   * - `silent` keeps the rows on screen without the loading flag (a realtime refresh); `limit` asks
   *   for more than one page (so a refresh keeps the rows already scrolled into).
   * - A first-page failure sets `error`. It keeps the rows only when they are the same list (a
   *   refresh or a retry); a failed load of a different list (another tab, another org) clears them,
   *   so the page shows the full error state instead of the wrong list.
   * - A failed append sets `moreError` instead (the rows and the cursor stay, so it can be retried);
   *   the next append or any first page clears it.
   */
  async function fetchThreads(
    state: InboxState,
    opts: { append?: boolean; silent?: boolean; limit?: number } = {},
  ): Promise<boolean> {
    if (opts.append) {
      const cursor = nextCursor.value
      if (!cursor || firstPagePending || loadingMore.value) return false
      const seq = listSeq
      loadingMore.value = true
      moreError.value = null
      try {
        const page = await inboxApi.listThreads(listParams(state, cursor))
        if (seq !== listSeq) return false
        const have = new Set(threads.value.map((t) => t.key))
        threads.value = [...threads.value, ...page.data.filter((t) => !have.has(t.key))]
        nextCursor.value = page.nextCursor
        return true
      } catch (err) {
        if (seq === listSeq) moreError.value = apiErrorMessage(err, LOAD_MORE_FALLBACK)
        return false
      } finally {
        if (seq === listSeq) loadingMore.value = false
      }
    }

    const seq = ++listSeq
    const key = keyFor(state)
    firstPagePending = true
    loadingMore.value = false
    moreError.value = null
    if (!opts.silent) loading.value = true
    try {
      const params = listParams(state)
      if (opts.limit) params.limit = opts.limit
      const page = await inboxApi.listThreads(params)
      if (seq !== listSeq) return false
      threads.value = page.data
      nextCursor.value = page.nextCursor
      rowsKey = key
      error.value = null
      return true
    } catch (err) {
      if (seq !== listSeq) return false
      if (rowsKey !== key) {
        threads.value = []
        nextCursor.value = null
        rowsKey = key
      }
      error.value = apiErrorMessage(err, 'Could not load conversations.')
      return false
    } finally {
      if (seq === listSeq) {
        loading.value = false
        firstPagePending = false
      }
    }
  }

  /** Merge a `wa-thread-update` into the loaded rows; true when the thread is not among them (the
   *  caller decides whether to refetch). `openKey` is the thread open on screen (not counted unread). */
  function applyWaThreadUpdate(event: WaThreadUpdatePayload, openKey: string | null): boolean {
    const result = applyRealtime(threads.value, event, { openKey })
    if (result.rows !== threads.value) threads.value = result.rows
    return result.refetch
  }

  /** Change one loaded row in place (e.g. `{ unreadCount: 0 }` once a thread is opened, the new
   *  snippet and time after a reply); a key that is not loaded is ignored. */
  function patchThread(key: string, patch: Partial<Omit<InboxThreadRow, 'key'>>) {
    if (!threads.value.some((t) => t.key === key)) return
    threads.value = threads.value.map((t) => (t.key === key ? { ...t, ...patch, key: t.key } : t))
  }

  // ── counts ────────────────────────────────────────────────────────────

  let countsTimer: ReturnType<typeof setTimeout> | null = null
  let lastCountsAt = 0
  let countsSeq = 0
  let countsWaiters: Array<() => void> = []

  async function runCounts() {
    countsTimer = null
    lastCountsAt = Date.now()
    const waiters = countsWaiters
    countsWaiters = []
    const seq = ++countsSeq
    // The org the request goes out for: an answer that lands after a switch belongs to the old org.
    const org = activeOrg()
    try {
      const next = await inboxApi.counts()
      if (seq === countsSeq && org === activeOrg()) counts.value = next
    } catch {
      // Keep the last counts; until any load the nav badge falls back to the WhatsApp unread count (R32).
    } finally {
      for (const resolve of waiters) resolve()
    }
  }

  /**
   * Refresh the counts, debounced: the first call runs at once, later calls within 1.5 s of the
   * last request fold into one trailing request at the end of that window (so a burst of socket
   * events costs one request and the last event is always reflected). Resolves once the request
   * the call was folded into has settled; never rejects.
   */
  function fetchCounts(): Promise<void> {
    return new Promise<void>((resolve) => {
      countsWaiters.push(resolve)
      if (countsTimer) return
      const wait = Math.max(0, lastCountsAt + COUNTS_DEBOUNCE_MS - Date.now())
      countsTimer = setTimeout(() => void runCounts(), wait)
    })
  }

  /**
   * Forget the counts and every counts request of the previous org (called on an org switch by
   * resetUserScopedStores): an answer still in flight is dropped, a pending trailing request is
   * cancelled (its callers settle now) and the next `fetchCounts` runs at once. The debounce timer
   * and sequence live outside the store's state, so the generic `$reset` cannot do this.
   */
  function resetCounts() {
    countsSeq += 1
    if (countsTimer) clearTimeout(countsTimer)
    countsTimer = null
    lastCountsAt = 0
    const waiters = countsWaiters
    countsWaiters = []
    for (const resolve of waiters) resolve()
    counts.value = null
  }

  /** Move a waiting thread in or out of the counts (matched threads only), clamped at zero. */
  function adjustCounts(row: InboxThreadRow, delta: 1 | -1) {
    if (!counts.value || row.unmatched) return
    const c = { ...counts.value }
    c[row.channel] = Math.max(0, c[row.channel] + delta)
    c.all = Math.max(0, c.all + delta)
    counts.value = c
  }

  // ── done ──────────────────────────────────────────────────────────────

  /**
   * The done toggle (`markDone` / `clearDone`), optimistic: the row and the counts change at once,
   * the server's answer confirms (`doneAt`) and a counts refresh follows. On failure both roll back
   * (unless the row changed again meanwhile), the error is TOASTED HERE (except the demo org's
   * write block, which lib/api.ts already toasted) and then RETHROWN: callers catch it to stop
   * their own follow-up, and must not toast it again.
   */
  async function setDone(key: string, done: boolean): Promise<InboxDoneResult> {
    const before = threads.value.find((t) => t.key === key) ?? null
    const optimisticDoneAt = done ? new Date().toISOString() : null
    // Undo: an inbound last message waits again (the server decides exactly; the next list refresh
    // corrects an auto-reply, which never counts as waiting).
    const optimisticNeedsReply = done ? false : !!before && !before.unmatched && before.lastDirection === 'inbound'
    if (before) {
      patchThread(key, { doneAt: optimisticDoneAt, needsReply: optimisticNeedsReply })
      if (before.needsReply !== optimisticNeedsReply) adjustCounts(before, optimisticNeedsReply ? 1 : -1)
    }
    try {
      const result = done ? await inboxApi.markDone(key) : await inboxApi.clearDone(key)
      if (threads.value.some((t) => t.key === key && t.doneAt === optimisticDoneAt)) patchThread(key, { doneAt: result.doneAt })
      void fetchCounts()
      return result
    } catch (err) {
      const now = threads.value.find((t) => t.key === key)
      if (before && now && now.doneAt === optimisticDoneAt && now.needsReply === optimisticNeedsReply) {
        patchThread(key, { doneAt: before.doneAt, needsReply: before.needsReply })
        if (before.needsReply !== optimisticNeedsReply) adjustCounts(before, before.needsReply ? 1 : -1)
      }
      if (!isDemoRejection(err)) {
        notify.error(apiErrorMessage(err, done ? 'Could not mark the conversation done.' : 'Could not undo done.'))
      }
      throw err
    }
  }

  const markDone = (key: string) => setDone(key, true)
  const clearDone = (key: string) => setDone(key, false)

  return {
    threads,
    nextCursor,
    counts,
    loading,
    loadingMore,
    error,
    moreError,
    fetchThreads,
    applyWaThreadUpdate,
    patchThread,
    fetchCounts,
    resetCounts,
    markDone,
    clearDone,
  }
})
